import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, MoreHorizontal, Filter, Users as UsersIcon, Calendar, CheckCircle, XCircle, UserCheck, UserX, Eye, X, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUsers, createUser, updateUser, deleteUser, loginAsUser } from '../utils/userApi';
import { getSiteMeta } from '../utils/siteMetaApi';
import { getSites } from '../utils/siteApi';
import { mockUsers, mockUserRoles, mockUserStatuses } from '../utils/mockUserData';
import { mockSites } from '../utils/mockSiteData';
import { apiCall } from '../utils/api';
import { SEND_MAIL } from '../config/api';


const ITEMS_PER_PAGE = 10;

const Users = () => {
  const { user: currentUser, login } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]); // Start with empty array
  const [sites, setSites] = useState([]); // Start with empty array
  const [loading, setLoading] = useState(true); // Start with true to show loading
  const [sitesLoading, setSitesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [apiResponse, setApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState(null);
  const buildFormData = (overrides = {}) => ({
    fname: '',
    lname: '',
    email: '',
    role: 'user',
    status: 'active',
    company: '',
    phone: '',
    job_title: '',
    site_id: '',
    full_access: '1',
    password: '',
    ...overrides
  });

  const generateRandomPassword = (length = 8) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let result = '';
    for (let i = 0; i < length; i += 1) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      result += characters.charAt(randomIndex);
    }
    return result;
  };

  const [formData, setFormData] = useState(buildFormData());
  
       // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Dynamic column configuration based on API response
  const getTableColumns = () => {
    if (safeUsers.length === 0) return [];
    
    // Get the first user to analyze the structure
    const sampleUser = safeUsers[0];
    const columns = [];
    
    // Helper function to detect field names with fallbacks
    const getFieldValue = (user, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (user[name] !== undefined && user[name] !== null && user[name] !== '') {
          return user[name];
        }
      }
      return defaultValue;
    };
    
    // Always include actions as first column
    columns.push({
      key: 'actions',
      label: '',
      type: 'actions',
      width: 'w-32'
    });
    
    // Always include user info column
    columns.push({
      key: 'user',
      label: 'User',
      type: 'user',
      width: 'w-48'
    });
    
    // Always include email (with fallback names)
    columns.push({
      key: 'email',
      label: 'Email',
      type: 'text',
      width: 'w-48'
    });
    
    // Conditionally include site if available (moved after Email)
    const siteId = getFieldValue(sampleUser, ['site_id']);
    if (siteId) {
      columns.push({
        key: 'site_id',
        label: 'Site',
        type: 'site',
        width: 'w-40'
      });
    }
    
    // Always include role
    columns.push({
      key: 'role',
      label: 'Role',
      type: 'badge',
      width: 'w-24'
    });
    
    // Always include status
    columns.push({
      key: 'status',
      label: 'Status',
      type: 'badge',
      width: 'w-24'
    });
    
    // Always include action
    columns.push({
      key: 'event_status',
      label: 'Event Action',
      type: 'text',
      width: 'w-32'
    });
    
    // Always include opened
    columns.push({
      key: 'opened',
      label: 'Opened',
      type: 'open',
      width: 'w-20'
    });
    
    // Always include clicked
    columns.push({
      key: 'clicked',
      label: 'Clicked',
      type: 'click',
      width: 'w-20'
    });
    
    // Conditionally include phone if available (with fallback names)
    const phone = getFieldValue(sampleUser, ['phone', 'phone_number', 'mobile', 'contact_number']);
    if (phone) {
      columns.push({
        key: 'phone',
        label: 'Phone',
        type: 'text',
        width: 'w-32'
      });
    }
    
    // Always include date added (with fallback names)
    columns.push({
      key: 'date_added',
      label: 'Date Added',
      type: 'date',
      width: 'w-32'
    });
    
    // Conditionally include last login if available (with fallback names)
    const lastLogin = getFieldValue(sampleUser, ['last_login', 'last_login_at', 'last_activity', 'last_seen']);
    if (lastLogin) {
      columns.push({
        key: 'last_login',
        label: 'Last Login',
        type: 'datetime',
        width: 'w-40'
      });
    }
    
    return columns;
  };

  // Get dynamic columns
  const tableColumns = getTableColumns();

  // Render table cell based on column type
  const renderTableCell = (column, user) => {
    // Helper function to get field value with fallbacks
    const getFieldValue = (user, possibleNames, defaultValue = null) => {
      for (const name of possibleNames) {
        if (user[name] !== undefined && user[name] !== null && user[name] !== '') {
          return user[name];
        }
      }
      return defaultValue;
    };

    switch (column.type) {
      
      case 'user':
        const fname = getFieldValue(user, ['fname', 'first_name', 'firstname', 'name']);
        const lname = getFieldValue(user, ['lname', 'last_name', 'lastname', 'surname']);
        const jobTitle = getFieldValue(user, ['job_title', 'department', 'dept', 'division', 'team']);
        
        return (
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {(fname && fname.charAt(0)) || (lname && lname.charAt(0)) || 'U'}
              </span>
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-900">
                {fname && lname ? `${fname} ${lname}` : (fname || lname || 'No Name')}
              </div>
              <div className="text-sm text-gray-500">{jobTitle || 'No Job Title'}</div>
            </div>
          </div>
        );
      
      case 'badge':
        if (column.key === 'role') {
          const role = getFieldValue(user, ['role', 'user_role', 'permission_level']);
          return getRoleBadge(role);
        } else if (column.key === 'status') {
          const status = getFieldValue(user, ['status', 'user_status', 'status']);
          return getStatusBadge(status);
        }
        return null;
      
      case 'boolean':
        // For boolean values, check directly to handle false values correctly
        // Treat 1, '1', true, 'true' as true; everything else as false
        const rawValue = user[column.key];
        
        // Check if value should be true: 1, '1', true, or 'true'
        let isTrue = false;
        if (rawValue === 1) {
          isTrue = true;
        } else if (rawValue === '1') {
          isTrue = true;
        } else if (rawValue === true) {
          isTrue = true;
        } else if (typeof rawValue === 'string' && rawValue.trim() === '1') {
          isTrue = true;
        } else if (typeof rawValue === 'string' && rawValue.toLowerCase().trim() === 'true') {
          isTrue = true;
        } else if (typeof rawValue === 'string' && !isNaN(rawValue.trim()) && Number(rawValue.trim()) === 1) {
          isTrue = true;
        }
        
        return (
          <div className="flex items-center" >
            {1 ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
          </div>
        );

      
      case 'open':
        const iconValue = getFieldValue(user, ['opened', 'open']);
        return (
          <div className="flex items-center">
            {iconValue ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
          </div>
        );

        case 'click':
        const clickValue = getFieldValue(user, ['clicked']);
        return (
          <div className="flex items-center">
            {clickValue ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-gray-400" />
            )}
          </div>
        );
      
      case 'date':
        const dateValue = getFieldValue(user, [column.key, 'created_at', 'created_date']);
        // Format date to show only date (no time)
        let formattedDate = 'No Date';
        if (dateValue) {
          try {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toLocaleDateString();
            } else if (typeof dateValue === 'string' && dateValue.includes('-')) {
              // If it's already in YYYY-MM-DD format, use it as is
              formattedDate = dateValue.split('T')[0];
            } else {
              formattedDate = dateValue;
            }
          } catch (e) {
            formattedDate = dateValue;
          }
        }
        return (
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-500">{formattedDate}</span>
          </div>
        );
      
      case 'datetime':
        const datetimeValue = getFieldValue(user, [column.key, 'updated_at', 'modified_at']);
        return (
          <div className="text-sm text-gray-500">
            {datetimeValue ? new Date(datetimeValue).toLocaleString() : 'Never'}
          </div>
        );
      
      case 'site':
        const siteId = getFieldValue(user, ['site_id']);
        // Find site by matching either site_id or id field
        const site = siteId ? sites.find(s => {
          const sId = s.site_id || s.id;
          return sId && (parseInt(sId) === parseInt(siteId) || String(sId) === String(siteId));
        }) : null;
        return (
          <div className="text-sm text-gray-500">
            {site ? (site.site_name || site.name || site.site_account || 'Unknown Site') : 'No Site Assigned'}
          </div>
        );
      
      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(user);
              }}
              className="text-blue-600 hover:text-blue-900"
              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
              title="View Details"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(user);
              }}
              className="text-primary-600 hover:text-primary-900"
              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
              title="Edit User"
            >
              <Edit className="h-4 w-4" />
            </button>
            {/* Login As button - only show for admin users */}
            {currentUser?.role === 'admin' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleLoginAs(user);
                }}
                className="text-green-600 hover:text-green-900"
                style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                title="Login As"
              >
                <LogIn className="h-4 w-4" />
              </button>
            )}
            {/**<button
              onClick={() => handleDelete(user.id)}
              className="text-red-600 hover:text-red-900"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreHorizontal className="h-4 w-4" />
            </button>*/}
          </div>
        );
      
      default:
        // For text fields, try to get the value with fallbacks
        const textValue = getFieldValue(user, [column.key]);
        return (
          <span className="text-sm text-gray-500">
            {textValue || `No ${column.label}`}
          </span>
        );
    }
  };




  // Fetch users and sites on component mount
  useEffect(() => {
    fetchUsers();
    fetchSites();
  }, []);



    const fetchUsers = async () => {
    setLoading(true);
    try {
      // Try to fetch from API first
      const response = await getUsers();
      
      // Always store the API response for display
      setApiResponse(response);
      
      if (response.success) {
        // Check if response.data exists and handle different data structures
        let userData = response.data;
        
        // Handle different possible API response structures
        if (response.data && response.data.data) {
          // If API returns { data: { data: [...] } }
          userData = response.data.data;
        } else if (response.data && response.data.users) {
          // If API returns { data: { users: [...] } }
          userData = response.data.users;
        } else if (response.data && response.data.items) {
          // If API returns { data: { items: [...] } }
          userData = response.data.items;
        } else if (response.data && response.data.results) {
          // If API returns { data: { results: [...] } }
          userData = response.data.results;
        } else if (response.data && response.data.list) {
          // If API returns { data: { list: [...] } }
          userData = response.data.list;
        } else if (response.data && response.data.records) {
          // If API returns { data: { records: [...] } }
          userData = response.data.records;
        }
        
        if (Array.isArray(userData)) {
          console.log('Setting users from API:', userData);
          setUsers(userData);
        } else {
          console.warn('API returned data but it\'s not an array:', userData);
          setUsers([]); // Set empty array instead of mock data
        }
      } else {
        // API call failed, set empty array
        console.warn('API call failed:', response.error);
        setUsers([]);
      }
    } catch (error) {
      // API call threw an error, set empty array
      console.warn('API error:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    setSitesLoading(true);
    try {
      // Try to fetch from API first
      const response = await getSites();
      
      if (response.success) {
        // Check if response.data exists and handle different data structures
        let siteData = response.data;
        
        // Handle different possible API response structures
        if (response.data && response.data.data) {
          // If API returns { data: { data: [...] } }
          siteData = response.data.data;
        } else if (response.data && response.data.sites) {
          // If API returns { data: { sites: [...] } }
          siteData = response.data.sites;
        } else if (response.data && response.data.items) {
          // If API returns { data: { items: [...] } }
          siteData = response.data.items;
        } else if (response.data && response.data.results) {
          // If API returns { data: { results: [...] } }
          siteData = response.data.results;
        } else if (response.data && response.data.list) {
          // If API returns { data: { list: [...] } }
          siteData = response.data.list;
        } else if (response.data && response.data.records) {
          // If API returns { data: { records: [...] } }
          siteData = response.data.records;
        }
        
        if (Array.isArray(siteData)) {
          console.log('Setting sites from API:', siteData);
          setSites(siteData);
        } else {
          console.warn('API returned sites data but it\'s not an array:', siteData);
          setSites([]); // Set empty array instead of mock data
        }
      } else {
        // API call failed, set empty array
        console.warn('Sites API call failed:', response.error);
        setSites([]);
      }
    } catch (error) {
      // API call threw an error, set empty array
      console.warn('Sites API error:', error);
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotification(null);
    
    const submissionData = { ...formData };
    if (!submissionData.password) {
      delete submissionData.password;
    }
    
    // Ensure site_id is included when updating user
    // Convert empty string to null if no site is selected, or keep the value
    if (editingUser) {
      // Always include site_id in update, even if empty
      if (submissionData.site_id === '' || submissionData.site_id === null || submissionData.site_id === undefined) {
        submissionData.site_id = null;
      } else {
        // Ensure site_id is a number if it's a string
        submissionData.site_id = submissionData.site_id ? parseInt(submissionData.site_id) : null;
      }
    }

    try {
      let response;
      if (editingUser) {
        setNotification({ type: 'sending', message: 'Updating user...' });
        console.log('🔄 Updating user with data:', submissionData);
        console.log('📍 Site ID being sent:', submissionData.site_id);
        response = await updateUser(editingUser.id, submissionData);
      } else {
        setNotification({ type: 'sending', message: 'Creating user...' });
        response = await createUser(submissionData);
      }

      if (response.success) {
        // Get opened and clicked values from response or formData
        const userData = response.data?.user || response.data?.data || response.data || {};
        const opened = userData.opened !== undefined ? userData.opened : (formData.opened !== undefined ? formData.opened : null);
        const clicked = userData.clicked !== undefined ? userData.clicked : (formData.clicked !== undefined ? formData.clicked : null);
        
        // Determine notification type based on opened and clicked values
        let notificationType = 'success';
        let statusInfo = '';
        
        if (opened === 1 || opened === '1' || opened === true) {
          notificationType = 'success';
          statusInfo += 'Opened ✓';
        } else if (opened === 0 || opened === '0' || opened === false) {
          statusInfo += 'Not Opened';
        }
        
        if (clicked === 1 || clicked === '1' || clicked === true) {
          if (statusInfo) statusInfo += ' | ';
          statusInfo += 'Clicked ✓';
        } else if (clicked === 0 || clicked === '0' || clicked === false) {
          if (statusInfo) statusInfo += ' | ';
          statusInfo += 'Not Clicked';
        }
        
        // Show success notification with opened/clicked status
        let message = '';
        if (editingUser) {
          message = `User ${formData.fname || formData.email || 'updated'} updated successfully!`;
        } else {
          message = `User ${formData.fname || formData.email || 'created'} created successfully!`;
        }
        
        if (statusInfo) {
          message += ` (${statusInfo})`;
        }
        
        setNotification({ 
          type: notificationType, 
          message: message,
          opened: opened,
          clicked: clicked
        });
        setTimeout(() => setNotification(null), 5000);
        
        // Send email when a new user is created
        if (!editingUser && formData.email) {
          try {
            console.log('📧 Sending new-user email to:', formData.email);
            const emailResponse = await apiCall(SEND_MAIL, {
              method: 'POST',
              data: {
                type: 'new-user',
                email: formData.email,
                site_id: formData.site_id || currentUser?.site_id || '',
                firstname: formData.fname || '',
                lastname: formData.lname || '',
                password: formData.password || '',
                role: formData.role || 'user',
                company: formData.company || '',
                phone: formData.phone || '',
                job_title: formData.job_title || ''
              }
            });
            
            console.log('📨 Email API response:', emailResponse);
            
            if (emailResponse.success) {
              console.log('✅ New-user email sent successfully');
            } else {
              console.warn('⚠️ Failed to send new-user email:', emailResponse.error);
            }
          } catch (emailError) {
            console.error('❌ Error sending new-user email:', emailError);
            // Don't block user creation if email fails
          }
        }
        
        setShowModal(false);
        setEditingUser(null);
        resetForm();
        fetchUsers();
      } else {
        // Show error notification if API call failed
        if (editingUser) {
          setNotification({ type: 'error', message: 'Failed to update user: ' + (response.error || 'Unknown error') });
        } else {
          setNotification({ type: 'error', message: 'Failed to create user: ' + (response.error || 'Unknown error') });
        }
        setTimeout(() => setNotification(null), 5000);
        
        // Fallback to mock data operations if API fails
        if (editingUser) {
          // Update mock data
          setUsers(prev => prev.map(user => 
            user.id === editingUser.id 
              ? { ...user, ...submissionData, updated_at: new Date().toISOString() }
              : user
          ));
        } else {
          // Create new mock user
          const newUser = {
            id: safeUsers.length > 0 ? Math.max(...safeUsers.map(u => u.id)) + 1 : 1,
            ...submissionData,
            date_added: new Date().toISOString().split('T')[0],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          setUsers(prev => [...(Array.isArray(prev) ? prev : []), newUser]);
        }
        setShowModal(false);
        setEditingUser(null);
        resetForm();
      }
    } catch (error) {
      // Show error notification
      if (editingUser) {
        setNotification({ type: 'error', message: 'Failed to update user: ' + error.message });
      } else {
        setNotification({ type: 'error', message: 'Failed to create user: ' + error.message });
      }
      setTimeout(() => setNotification(null), 5000);
      
      // Fallback to mock data operations if API throws error
      if (editingUser) {
        setUsers(prev => prev.map(user => 
          user.id === editingUser.id 
            ? { ...user, ...submissionData, updated_at: new Date().toISOString() }
            : user
        ));
      } else {
        const newUser = {
          id: safeUsers.length > 0 ? Math.max(...safeUsers.map(u => u.id)) + 1 : 1,
          ...submissionData,
          date_added: new Date().toISOString().split('T')[0],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setUsers(prev => [...(Array.isArray(prev) ? prev : []), newUser]);
      }
      setShowModal(false);
      setEditingUser(null);
      resetForm();
    }
  };

  const handleGeneratePassword = () => {
    setFormData(prev => ({
      ...prev,
      password: generateRandomPassword()
    }));
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
  };

  const fetchSiteLogo = async (siteId) => {
    if (!siteId) return null;
    try {
      const response = await getSiteMeta(siteId);
      if (response.success) {
        const metaData = response.data?.data || response.data;
        return metaData?.logo || metaData?.site_logo || null;
      }
    } catch (error) {
      console.error('Error fetching site logo:', error);
    }
    return null;
  };

  const handleLoginAs = async (targetUser) => {
    if (!window.confirm(`Are you sure you want to login as ${targetUser.fname || ''} ${targetUser.lname || ''} (${targetUser.email || ''})?`)) {
      return;
    }

    try {
      const userId = targetUser.id || targetUser.user_id;
      if (!userId) {
        alert('Error: User ID is missing. Cannot login as this user.');
        return;
      }

      const response = await loginAsUser(userId);
      
      if (response.success) {
        const data = response.data;
        console.log('🔍 Login-as API response data:', data);
        
        // Extract user data and token from response - handle different response structures
        const userData = {
          id: data.user?.id || data.data?.user?.id || data.id || targetUser.id,
          name: data.user?.name || data.data?.user?.name || data.name || `${targetUser.fname || ''} ${targetUser.lname || ''}`.trim() || 'User',
          email: data.user?.email || data.data?.user?.email || data.email || targetUser.email,
          site_id: data.user?.site_id || data.data?.user?.site_id || data.site_id || targetUser.site_id,
          role: data.user?.role || data.data?.user?.role || data.role || targetUser.role,
          full_access: data.user?.full_access || data.data?.user?.full_access || data.full_access || targetUser.full_access || '1',
          ...data.data?.user,
          ...data.user,
          ...data
        };
        
        const token = data.token || data.data?.token;
        
        // Extract sites data - handle different response structures
        let sites = [];
        if (data.data?.user?.sites) {
          sites = Array.isArray(data.data.user.sites) ? data.data.user.sites : [data.data.user.sites];
        } else if (data.user?.sites) {
          sites = Array.isArray(data.user.sites) ? data.user.sites : [data.user.sites];
        } else if (data.data?.sites) {
          sites = Array.isArray(data.data.sites) ? data.data.sites : [data.data.sites];
        } else if (data.sites) {
          sites = Array.isArray(data.sites) ? data.sites : [data.sites];
        } else if (targetUser.sites) {
          sites = Array.isArray(targetUser.sites) ? targetUser.sites : [targetUser.sites];
        }
        
        // If sites is empty but user has site_id, try to construct site object
        if (sites.length === 0 && (userData.site_id || targetUser.site_id)) {
          const siteId = userData.site_id || targetUser.site_id;
          sites = [{
            site_id: siteId,
            site_name: userData.site_name || targetUser.site_name || 'Site',
            logo: userData.logo || targetUser.logo || null
          }];
        }
        
        console.log('🔍 Extracted sites data:', sites);
        console.log('🔍 Extracted token:', token);
        
        // Ensure each site has a logo by fetching site meta if needed
        if (sites.length > 0) {
          for (let i = 0; i < sites.length; i += 1) {
            const site = sites[i];
            if (site && !site.logo && !site.site_logo && site.site_id) {
              const logo = await fetchSiteLogo(site.site_id);
              if (logo) {
                sites[i] = { ...site, logo };
              }
            }
          }
        } else if (userData.site_id) {
          const logo = await fetchSiteLogo(userData.site_id);
          sites = [{
            site_id: userData.site_id,
            site_name: userData.site_name || targetUser.site_name || 'Site',
            logo
          }];
        }

        if (token) {
          // Login as the target user (pass true to indicate impersonation)
          login(userData, token, sites, true);
          // Redirect to dashboard
          navigate('/admin/dashboard', { replace: true });
        } else {
          alert('Error: No token received from server. Cannot login as this user.');
        }
      } else {
        const errorMessage = response.error || response.message || 'Failed to login as user. Please try again.';
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error logging in as user:', error);
      alert(`Error: ${error.message || 'An error occurred while trying to login as this user.'}`);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    // Convert site_id to string for the select input, handling both number and string types
    const siteId = user.site_id !== null && user.site_id !== undefined 
      ? String(user.site_id) 
      : '';
    setFormData(buildFormData({
      fname: user.fname || '',
      lname: user.lname || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active',
      company: user.company || '',
      phone: user.phone || '',
      job_title: user.job_title || '',
      site_id: siteId,
      full_access: user.full_access ? String(user.full_access) : '1'
    }));
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await deleteUser(userId);
        if (response.success) {
          fetchUsers();
        } else {
          // Fallback to mock data operations if API fails
          setUsers(prev => prev.filter(user => user.id !== userId));
        }
      } catch (error) {
        // Fallback to mock data operations if API throws error
        setUsers(prev => prev.filter(user => user.id !== userId));
      }
    }
  };

  const resetForm = () => {
    setFormData(buildFormData());
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(buildFormData({ password: generateRandomPassword() }));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    resetForm();
  };

   const filteredUsers = searchTerm ? safeUsers.filter(user => {
     // Helper function to get field value with fallbacks
     const getFieldValue = (user, possibleNames) => {
       for (const name of possibleNames) {
         if (user[name] !== undefined && user[name] !== null && user[name] !== '') {
           return user[name];
         }
       }
       return null;
     };

     const fname = getFieldValue(user, ['fname', 'first_name', 'firstname', 'name']);
     const lname = getFieldValue(user, ['lname', 'last_name', 'lastname', 'surname']);
     const email = getFieldValue(user, ['email', 'email_address', 'user_email']);
     const company = getFieldValue(user, ['company', 'company_code', 'org_code']);
     const jobTitle = getFieldValue(user, ['job_title', 'department', 'dept', 'division', 'team']);

     const searchLower = searchTerm.toLowerCase();
     
     return (
       (fname && fname.toLowerCase().includes(searchLower)) ||
       (lname && lname.toLowerCase().includes(searchLower)) ||
       (email && email.toLowerCase().includes(searchLower)) ||
       (company && company.toLowerCase().includes(searchLower)) ||
       (jobTitle && jobTitle.toLowerCase().includes(searchLower))
     );
   }) : safeUsers;
   const totalResults = filteredUsers.length;
   const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);
   const effectiveCurrentPage = totalPages > 0 ? Math.min(currentPage, totalPages) : 1;
   const startIndex = totalPages > 0 ? (effectiveCurrentPage - 1) * ITEMS_PER_PAGE : 0;
   const endIndex = totalResults > 0 ? Math.min(startIndex + ITEMS_PER_PAGE, totalResults) : 0;
   const paginatedUsers = totalResults > 0 ? filteredUsers.slice(startIndex, endIndex) : [];
   const canGoPrevious = totalResults > 0 && effectiveCurrentPage > 1;
   const canGoNext = totalResults > 0 && effectiveCurrentPage < totalPages;
   const displayFrom = totalResults === 0 ? 0 : startIndex + 1;
   const displayTo = totalResults === 0 ? 0 : endIndex;
   const pageDisplay = totalPages === 0 ? 0 : effectiveCurrentPage;

   useEffect(() => {
     if (totalPages === 0 && currentPage !== 1) {
       setCurrentPage(1);
     } else if (totalPages > 0 && currentPage > totalPages) {
       setCurrentPage(totalPages);
     }
   }, [totalPages, currentPage]);


  const getRoleBadge = (role) => {
    const fallbackRole = mockUserRoles[mockUserRoles.length - 1];
    const roleConfig = mockUserRoles.find(r => r.value === role) || fallbackRole; // Default to last role (user)
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleConfig.color}`}>
        {roleConfig.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = mockUserStatuses.find(s => s.value === status) || mockUserStatuses[1]; // Default to 'inactive'
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg animate-in slide-in-from-top-5 ${
          notification.type === 'success' ? 'bg-green-50 border-2 border-green-400' :
          notification.type === 'error' ? 'bg-red-50 border-2 border-red-400' :
          notification.type === 'sending' ? 'bg-blue-50 border-2 border-blue-400' :
          'bg-gray-50 border-2 border-gray-400'
        }`}>
          <div className="flex items-center">
            {notification.type === 'success' && (
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
            )}
            {notification.type === 'error' && (
              <XCircle className="h-6 w-6 text-red-600 mr-3" />
            )}
            {notification.type === 'sending' && (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            )}
            <div className="flex flex-col">
              <span className={`text-base font-semibold ${
                notification.type === 'success' ? 'text-green-900' :
                notification.type === 'error' ? 'text-red-900' :
                notification.type === 'sending' ? 'text-blue-900' :
                'text-gray-900'
              }`}>
                {notification.message}
              </span>
              {/* Show opened and clicked status if available */}
              {(notification.opened !== undefined || notification.clicked !== undefined) && (
                <div className="flex items-center space-x-4 mt-2">
                  {notification.opened !== undefined && (
                    <div className="flex items-center space-x-1">
                      {notification.opened === 1 || notification.opened === '1' || notification.opened === true ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-xs text-gray-600">Opened</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-gray-400" />
                          <span className="text-xs text-gray-600">Not Opened</span>
                        </>
                      )}
                    </div>
                  )}
                  {notification.clicked !== undefined && (
                    <div className="flex items-center space-x-1">
                      {notification.clicked === 1 || notification.clicked === '1' || notification.clicked === true ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-xs text-gray-600">Clicked</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-gray-400" />
                          <span className="text-xs text-gray-600">Not Clicked</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-600">Manage your application users and their permissions.</p>
        </div>
        <button 
          onClick={openCreateModal}
          className="btn-primary flex items-center space-x-2 mt-4 sm:mt-0"
          style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
        >
          <Plus className="h-5 w-5" />
          <span>Add User</span>
        </button>
      </div>

      

      

       

             {/* API Response Debug 
       {apiResponse && (
         <div className="card bg-blue-50 border-blue-200">
           <div className="flex items-center justify-between mb-4">
             <h3 className="text-lg font-medium text-blue-900">🔍 API Response Debug</h3>
             <button
               onClick={() => setApiResponse(null)}
               className="text-blue-600 hover:text-blue-800 text-sm"
             >
               Hide Debug Info
             </button>
           </div>
           <div className="space-y-3">
             <div>
               <span className="font-medium text-blue-800">Status: </span>
               <span className={`px-2 py-1 rounded text-xs font-medium ${
                 apiResponse.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
               }`}>
                 {apiResponse.success ? 'Success' : 'Failed'}
               </span>
             </div>
             {apiResponse.error && (
               <div>
                 <span className="font-medium text-blue-800">Error: </span>
                 <span className="text-red-600 text-sm">{apiResponse.error}</span>
               </div>
             )}
             <div>
               <span className="font-medium text-blue-800">Data Type: </span>
               <span className="text-green-600 text-sm">
                 {typeof apiResponse.data} {Array.isArray(apiResponse.data) ? '(Array)' : '(Not Array)'}
               </span>
             </div>
             {apiResponse.data && (
               <div>
                 <span className="font-medium text-blue-800">Data Length: </span>
                 <span className="text-green-600 text-sm">
                   {Array.isArray(apiResponse.data) ? apiResponse.data.length : 'N/A'}
                 </span>
               </div>
             )}
             <div className="bg-white p-3 rounded border">
               <span className="font-medium text-blue-800 block mb-2">Raw Response:</span>
               <pre className="text-xs text-gray-700 overflow-x-auto bg-gray-50 p-2 rounded">
                 {JSON.stringify(apiResponse, null, 2)}
               </pre>
             </div>
           </div>
         </div>
       )}
*/}
       {/* Table Columns Debug 
       <div className="card bg-green-50 border-green-200">
         <div className="flex items-center justify-between mb-4">
           <h3 className="text-lg font-medium text-green-900">📊 Table Columns Debug</h3>
           <button
             onClick={fetchUsers}
             className="btn-secondary flex items-center space-x-2"
             title="Refresh API data"
           >
             <span>🔄</span>
             <span>Refresh</span>
           </button>
         </div>
         <div className="space-y-3">
           <div>
             <span className="font-medium text-green-800">Total Columns: </span>
             <span className="text-green-600 text-sm font-bold">{tableColumns.length}</span>
           </div>
           <div>
             <span className="font-medium text-green-800">Users Count: </span>
             <span className="text-green-600 text-sm font-bold">{safeUsers.length}</span>
           </div>
           <div className="bg-white p-3 rounded border">
             <span className="font-medium text-green-800 block mb-2">Column Configuration:</span>
             <div className="space-y-2">
               {tableColumns.map((column, index) => (
                 <div key={column.key} className="flex items-center space-x-2 text-xs">
                   <span className="font-medium text-gray-700">{index + 1}.</span>
                   <span className="text-gray-600">{column.label || column.key}</span>
                   <span className="text-gray-500">({column.type})</span>
                   <span className="text-gray-400">[{column.width}]</span>
                 </div>
               ))}
             </div>
           </div>
         </div>
       </div>
*/}
       {/* Filters and Search */}
       <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          {/**<div className="flex items-center space-x-3">
            <button className="btn-secondary flex items-center space-x-2">
              <Filter className="h-4 w-4" />
              <span>Filter</span>
            </button>
            
          </div>*/}
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
                         <div>
               <h3 className="text-lg font-medium text-gray-900">Users List</h3>
               <p className="text-sm text-gray-600">
                 {apiResponse && apiResponse.success 
                   ? `Showing ${safeUsers.length} users `//from API with ${tableColumns.length} columns` 
                   : `Showing ${safeUsers.length} users with ${tableColumns.length} columns (${safeUsers.length > 0 ? 'from fallback data' : 'no data available'})`
                 }
               </p>
             </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto table-container">
          <table className="min-w-full divide-y divide-gray-200">
                         <thead className="bg-gray-50">
               <tr>
                 {tableColumns.map((column) => (
                   <th 
                     key={column.key} 
                     className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                   >
                     {column.label}
                   </th>
                 ))}
               </tr>
             </thead>
                         <tbody className="bg-white divide-y divide-gray-200">
                                {safeUsers.length === 0 ? (
                   <tr>
                     <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                       <div className="flex flex-col items-center space-y-2">
                         <UsersIcon className="h-12 w-12 text-gray-300" />
                         <p className="text-lg font-medium">No users found</p>
                         <p className="text-sm">
                           {apiResponse && apiResponse.success 
                             ? 'API returned no data or empty array' 
                             : 'No API response or using fallback data'
                           }
                         </p>
                         <button
                           onClick={fetchUsers}
                           className="btn-secondary mt-2"
                           style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                         >
                           Try Again
                         </button>
                       </div>
                     </td>
                   </tr>
                 ) : filteredUsers.length === 0 ? (
                   <tr>
                     <td colSpan={tableColumns.length} className="px-6 py-8 text-center text-gray-500">
                       <div className="flex flex-col items-center space-y-2">
                         <Search className="h-12 w-12 text-gray-300" />
                         <p className="text-lg font-medium">No users match your search</p>
                         <p className="text-sm">Try adjusting your search terms</p>
                       </div>
                     </td>
                   </tr>
                 ) : (
                   paginatedUsers.map((user) => (
                     <tr 
                       key={user.id} 
                       className="hover:bg-gray-50 cursor-pointer"
                       onClick={() => handleViewDetails(user)}
                     >
                       {tableColumns.map((column) => (
                         <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                           {renderTableCell(column, user)}
                         </td>
                       ))}
                     </tr>
                   ))
                 )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between gap-4 sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={!canGoPrevious}
              className={`btn-secondary ${!canGoPrevious ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages || 1))}
              disabled={!canGoNext}
              className={`btn-secondary ${!canGoNext ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{displayFrom}</span> to <span className="font-medium">{displayTo}</span> of{' '}
                <span className="font-medium">{totalResults}</span> results
                {totalPages > 0 && (
                  <span className="ml-2">
                    Page <span className="font-medium">{pageDisplay}</span> of <span className="font-medium">{totalPages}</span>
                  </span>
                )}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!canGoPrevious}
                  className={`btn-secondary rounded-md ${!canGoPrevious ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Previous
                </button>
                <span className="px-4 py-2 bg-white text-sm font-medium text-gray-700 border border-gray-200 rounded-md">
                  {totalPages === 0 ? '0 / 0' : `${pageDisplay} / ${totalPages}`}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages || 1))}
                  disabled={!canGoNext}
                  className={`btn-secondary rounded-md ${!canGoNext ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingUser ? 'Edit User' : 'Create User'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.fname}
                      onChange={(e) => setFormData({...formData, fname: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.lname}
                      onChange={(e) => setFormData({...formData, lname: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!editingUser}
                    className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      editingUser ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                  {editingUser && (
                    <p className="text-xs text-gray-500 mt-1">
                      Email cannot be changed when updating a user.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      autoComplete="new-password"
                      placeholder={editingUser ? 'Leave blank to keep current password' : 'Generated password'}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white"
                      style={{ backgroundColor: '#d64a68' }}
                    >
                      Generate
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {editingUser ? 'Leave blank to retain the existing password.' : 'Share this generated password with the user after creation.'}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      {mockUserRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      {mockUserStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site
                  </label>
                  <select
                    value={formData.site_id || ''}
                    onChange={(e) => setFormData({...formData, site_id: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    disabled={sitesLoading}
                  >
                    <option value="">Select a Site</option>
                    {sites.map(site => {
                      // Handle both site_id and id fields
                      const siteId = site.site_id || site.id;
                      const siteName = site.site_name || site.name || 'Unnamed Site';
                      return (
                        <option key={siteId} value={siteId}>
                          {siteName}
                        </option>
                      );
                    })}
                  </select>
                  {sitesLoading && (
                    <p className="text-xs text-gray-500 mt-1">Loading sites...</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Access Level
                  </label>
                  <select
                    value={formData.full_access}
                    onChange={(e) => setFormData({...formData, full_access: e.target.value})}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="1">Stage One</option>
                    <option value="2">Stage Two</option>
                    <option value="3">Full Access</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select the access level for this user</p>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">User Details</h3>
                <button
                  onClick={closeDetailsModal}
                  className="text-gray-400 hover:text-gray-600"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Helper function to get field value with fallbacks */}
                {(() => {
                  const getFieldValue = (user, possibleNames, defaultValue = 'N/A') => {
                    for (const name of possibleNames) {
                      if (user[name] !== undefined && user[name] !== null && user[name] !== '') {
                        return user[name];
                      }
                    }
                    return defaultValue;
                  };

                  const fname = getFieldValue(selectedUser, ['fname', 'first_name', 'firstname', 'name']);
                  const lname = getFieldValue(selectedUser, ['lname', 'last_name', 'lastname', 'surname']);
                  const email = getFieldValue(selectedUser, ['email', 'email_address', 'user_email']);
                  const phone = getFieldValue(selectedUser, ['phone', 'phone_number', 'mobile', 'contact_number']);
                  const company = getFieldValue(selectedUser, ['company', 'company_code', 'org_code']);
                  const jobTitle = getFieldValue(selectedUser, ['job_title', 'department', 'dept', 'division', 'team']);
                  const role = getFieldValue(selectedUser, ['role', 'user_role', 'permission_level'], 'user');
                  const status = getFieldValue(selectedUser, ['status', 'user_status'], 'active');
                  const siteId = getFieldValue(selectedUser, ['site_id'], null);
                  const fullAccess = getFieldValue(selectedUser, ['full_access'], '1');
                  const dateAdded = getFieldValue(selectedUser, ['date_added', 'created_at', 'created_date']);
                  const lastLogin = getFieldValue(selectedUser, ['last_login', 'last_login_at', 'last_activity', 'last_seen']);
                  
                  const site = siteId && siteId !== 'N/A' ? sites.find(s => (s.site_id || s.id) === parseInt(siteId)) : null;

                  return (
                    <>
                      {/* Basic Information */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Basic Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">First Name</label>
                            <p className="text-sm text-gray-900">{fname}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Last Name</label>
                            <p className="text-sm text-gray-900">{lname}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                            <p className="text-sm text-gray-900">{email}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
                            <p className="text-sm text-gray-900">{phone}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Company</label>
                            <p className="text-sm text-gray-900">{company}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Job Title</label>
                            <p className="text-sm text-gray-900">{jobTitle}</p>
                          </div>
                        </div>
                      </div>

                      {/* Account Information */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Account Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
                            <div className="mt-1">
                              {getRoleBadge(role)}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                            <div className="mt-1">
                              {getStatusBadge(status)}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Access Level</label>
                            <p className="text-sm text-gray-900">
                              {fullAccess === '1' ? 'Stage One' : fullAccess === '2' ? 'Stage Two' : fullAccess === '3' ? 'Full Access' : fullAccess}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Site</label>
                            <p className="text-sm text-gray-900">
                              {site ? (site.site_name || site.name || 'N/A') : 'No Site Assigned'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Activity Information */}
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">Activity Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Date Added</label>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <p className="text-sm text-gray-900">
                                {dateAdded && dateAdded !== 'N/A' ? (dateAdded.includes('T') ? new Date(dateAdded).toLocaleDateString() : dateAdded) : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1">Last Login</label>
                            <p className="text-sm text-gray-900">
                              {lastLogin && lastLogin !== 'N/A' ? (lastLogin.includes('T') || lastLogin.includes(' ') ? new Date(lastLogin).toLocaleString() : lastLogin) : 'Never'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={closeDetailsModal}
                  className="btn-secondary"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeDetailsModal();
                    handleEdit(selectedUser);
                  }}
                  className="btn-primary"
                  style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                >
                  Edit User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
